import { useEffect, useState } from 'react'

import { useQuery, useMutation } from '@apollo/client'
import {
  Modal,
  Divider,
  Select,
  Textarea,
  Switch,
  Checkbox,
  Text,
  Tooltip,
  Group,
  useModalStackContext,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import dayjs from 'dayjs'
import { zodResolver } from 'mantine-form-zod-resolver'
import { z } from 'zod'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import Tag from 'src/components/DataDisplay/Tag/Tag'
import { toast } from 'src/components/Overlays/Toast/Toast'
import {
  CREATE_ADVERSE_ACTION_CASE,
  GET_ADVERSE_ACTION_CASES_BY_APPLICANT,
} from 'src/graphql/adverseActions.graphql'
import { GET_DOCUMENTS } from 'src/graphql/documents.graphql'
import { GET_EMPLOYEE_BY_ID } from 'src/graphql/employees.graphql'
import DateInput from 'src/components/Inputs/DateInput/DateInput'
import { DISQUALIFIED_REASON_OPTIONS } from 'src/lib/disqualifiedReason.utils'

const STACK_ID = 'disqualified-modal'

const DisqualifiedModal = ({ onSave, onClose, applicant }) => {
  const { currentUser } = useAuth()
  const userId = currentUser?.currentEmployeeId
  const stackCtx = useModalStackContext()

  // Mantine gap: Modal's stack-register useEffect has no cleanup, so
  // unmounting while opened leaks the stackId in Modal.Stack. Mirrors DeleteModal.
  useEffect(() => {
    return () => {
      if (stackCtx) {
        stackCtx.removeModal(STACK_ID)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [sendPreAdverseActionNotice, setSendPreAdverseActionNotice] =
    useState(false)
  const [selectedDocuments, setSelectedDocuments] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const labelClass = 'text-nowrap text-doubleNickel-gray-600'

  // Query for documents when pre-adverse action notice is checked
  const { data: documentsData } = useQuery(GET_DOCUMENTS, {
    variables: {
      applicantId: applicant?.applicantId,
      pageInfo: { limit: 100, offset: 0 },
    },
    skip: !sendPreAdverseActionNotice || !applicant?.applicantId,
  })

  const { data } = useQuery(GET_EMPLOYEE_BY_ID, {
    variables: {
      id: userId,
    },
  })

  const isEmailConnected = data?.employee?.emailConnected
  const hasApplicantEmail =
    applicant?.email && applicant.email.trim().length > 0
  const canSendAdverseAction = isEmailConnected && hasApplicantEmail

  // Function to get appropriate tooltip message for adverse action switch
  const getTooltipMessage = () => {
    if (!isEmailConnected && !hasApplicantEmail) {
      return 'Please connect your email in the settings page and ensure the applicant has an email address to enable this feature'
    }
    if (!isEmailConnected) {
      return 'Please connect your email in the settings page to enable this feature'
    }
    if (!hasApplicantEmail) {
      return 'Applicant must have an email address in order to send adverse action notices'
    }
    return ''
  }

  // Mutations
  const [createAdverseActionCase] = useMutation(CREATE_ADVERSE_ACTION_CASE, {
    refetchQueries: [
      {
        query: GET_ADVERSE_ACTION_CASES_BY_APPLICANT,
        variables: {
          applicantId: applicant?.applicantId,
        },
      },
    ],
    onError: () => {
      toast('Unable to configure adverse action case', 'error')
    },
  })

  const schema = z.object({
    disqualifiedReason: z
      .union([z.string(), z.null()])
      .refine((val) => val !== null && val.trim().length > 0, {
        message: 'Reason is required',
      }),
    isPermanentlyDisqualified: z
      .string()
      .refine((val) => val === 'true' || val === 'false', {
        message: 'Permanently disqualified status is required',
      }),
    content: z.string().min(0).max(250),
  })
  const form = useForm({
    initialValues: {
      applicantStageDate: dayjs(),
      disqualifiedReason: null,
      content: '',
      isPermanentlyDisqualified: 'false',
    },
    validate: zodResolver(schema),
  })

  const handleSubmit = async (values) => {
    setIsSubmitting(true)
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { isPermanentlyDisqualified, ...rest } = values

      // If pre-adverse action notice is checked, create adverse action case
      if (sendPreAdverseActionNotice) {
        await createAdverseActionCase({
          variables: {
            input: {
              applicantId: applicant?.applicantId,
              disqualifiedReason: values.disqualifiedReason,
              backgroundDocuments: selectedDocuments,
            },
          },
        })
      }

      onSave({
        ...rest,
        isPermanentlyDisqualified:
          isPermanentlyDisqualified === 'true' ||
          isPermanentlyDisqualified === true,
      })
      onClose()
    } catch (error) {
      console.error('Error creating adverse action case:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDocumentSelection = (documentId) => {
    setSelectedDocuments((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId]
    )
  }

  const reasons = DISQUALIFIED_REASON_OPTIONS

  // Handler to ensure Switch sends a string ("true"/"false") to the form
  const handleSwitchChange = (event) => {
    form.setFieldValue(
      'isPermanentlyDisqualified',
      event.currentTarget.checked ? 'true' : 'false'
    )
  }

  return (
    <Modal
      centered
      opened={true}
      onClose={onClose}
      classNames={{
        title: 'text-lg font-bold',
        header: 'border-b border-doubleNickel-gray-200',
      }}
      title="Disqualified"
      size={'50%'}
      stackId={STACK_ID}
      overlayProps={{
        backgroundOpacity: 0.3,
      }}
    >
      <form
        className="relative mt-4 flex flex-col gap-4"
        onSubmit={form.onSubmit(handleSubmit)}
      >
        <div className="max-h-[500px] flex-1 overflow-y-auto pr-2">
          <div className="flex flex-col gap-4">
            <div className="flex flex-row items-center justify-between">
              <Text size="sm" fw={500} className={labelClass}>
                Disqualification Date:
              </Text>
              <DateInput
                className="w-1/2"
                label=""
                value={form.values.applicantStageDate?.toDate?.() ?? form.values.applicantStageDate}
                onChange={(date) => form.setFieldValue('applicantStageDate', date)}
              />
            </div>
            <div className="flex flex-row items-center justify-between text-sm font-medium text-doubleNickel-gray-600">
              <Text size="sm" fw={500} className={labelClass}>
                Reason:
              </Text>
              <Select
                className="w-1/2"
                placeholder="Select a reason"
                data={reasons}
                searchable
                required
                {...form.getInputProps('disqualifiedReason')}
              />
            </div>

            <div className="flex flex-row items-start justify-between text-sm font-medium text-doubleNickel-gray-600">
              <Text size="sm" fw={500} className={`${labelClass} mt-1`}>
                Notes:
              </Text>
              <Textarea
                resize="vertical"
                classNames={{
                  root: 'w-1/2',
                  input: 'h-40',
                }}
                placeholder="Additional details or comments (optional)"
                {...form.getInputProps('content')}
              />
            </div>
            <div className="flex flex-row items-center justify-between text-sm font-medium text-doubleNickel-gray-600">
              <Text size="sm" fw={500} className={labelClass}>
                Permanently disqualify this applicant:
              </Text>
              <Switch
                checked={form.values.isPermanentlyDisqualified === 'true'}
                onChange={handleSwitchChange}
              />
            </div>
            <div className="flex flex-row items-start gap-2 text-sm font-medium text-doubleNickel-gray-600">
              <Text size="sm" fw={500} className={labelClass}>
                Send pre-adverse action notice:
              </Text>
              <Tooltip
                disabled={canSendAdverseAction}
                className="max-w-72 text-wrap"
                withArrow
                color="rgba(71, 84, 103, 0.9)"
                label={getTooltipMessage()}
              >
                <div className="ml-auto">
                  <Switch
                    disabled={!canSendAdverseAction}
                    checked={sendPreAdverseActionNotice}
                    onChange={(event) =>
                      setSendPreAdverseActionNotice(event.target.checked)
                    }
                  />
                </div>
              </Tooltip>
            </div>

            {/* Document selection when pre-adverse action notice is checked */}
            {sendPreAdverseActionNotice && (
              <div className="flex flex-col space-y-2 ">
                <Text size="sm" fw={500} className={labelClass}>
                  Select documents to attach to adverse action case:
                </Text>
                {documentsData?.documents?.items?.length > 0 ? (
                  <div className="space-y-1 p-1">
                    {documentsData.documents.items.map((document) => (
                      <Checkbox
                        key={document.documentId}
                        label={
                          <div className="flex flex-1 flex-row items-center gap-3">
                            <Text
                              size="sm"
                              className="text-doubleNickel-gray-500"
                            >
                              {document.fileName}
                            </Text>
                            <Tag
                              showCloseButton={false}
                              size="xs"
                              variant="secondary"
                            >
                              {document.category}
                            </Tag>
                          </div>
                        }
                        checked={selectedDocuments.includes(
                          document.documentId
                        )}
                        onChange={() =>
                          handleDocumentSelection(document.documentId)
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <Text size="sm" className="text-doubleNickel-gray-500">
                    No documents available for this applicant
                  </Text>
                )}
              </div>
            )}
          </div>
        </div>

        <Divider />
        <Group justify="flex-end" gap="xs">
          <DNButton
            variant="secondary"
            onClick={onClose}
            className="ml-auto w-24"
            disabled={isSubmitting}
          >
            Cancel
          </DNButton>
          <DNButton
            variant="primary"
            type="submit"
            className="w-24"
            loading={isSubmitting}
          >
            Save
          </DNButton>
        </Group>
      </form>
    </Modal>
  )
}

export default DisqualifiedModal
